import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Paper,
	Stack,
	Step,
	StepContent,
	StepLabel,
	Stepper,
	Tab,
	Tabs,
	Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplayIcon from "@mui/icons-material/Replay";
import { grey } from "@mui/material/colors";
import moment from "moment";
import { services } from "data/authData";
import { GlobalState } from "context/GlobalContext";
import { gigaCampaigns, gigaExplore, gigaGetRun, gigaInsights } from "./gigaApi";
import GigaExploreView from "./GigaExploreView";
import GigaInsightsView from "./GigaInsightsView";
import GigaCampaignsView from "./GigaCampaignsView";
import { GigaBusyOverlay, GigaRunPageSkeleton } from "./GigaSkeletons";

const GigaRunPage = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { snackBar, setSnackBar, setSnackBarOpen } = GlobalState();
	const [run, setRun] = useState(null);
	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState("");
	const [exploreRerunOpen, setExploreRerunOpen] = useState(false);
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [stepData, setStepData] = useState(null);

	const notify = (message, severity = "error") => {
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
		setSnackBarOpen(true);
	};

	const loadRun = async () => {
		setLoading(true);
		try {
			const result = await gigaGetRun(id);
			setRun(result);
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (id) loadRun();
	}, [id]);

	const handleExplore = async () => {
		setBusy("explore");
		try {
			const result = await gigaExplore({
				runId: id,
				seeds: run?.seeds,
				country: run?.country,
				language: run?.language,
				brandName: run?.brand_name,
				customerId: run?.ads_account?.customerId,
				limit: 5000,
			});
			setRun(result);
			setTab(0);
			setExploreRerunOpen(false);
			notify("Explore rerun. Insights and Campaigns were cleared.", "success");
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setBusy("");
		}
	};

	const handleInsights = async ({ question } = {}) => {
		setBusy("insights");
		try {
			const result = await gigaInsights({
				runId: id,
				language: "English",
				question,
			});
			setRun(result);
			setTab(1);
			notify(question ? "Follow-up answered." : "Insights generated.", "success");
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setBusy("");
		}
	};

	const handleInsightsReset = async () => {
		setBusy("insights");
		try {
			const result = await gigaInsights({ runId: id, reset: true });
			setRun(result);
			setTab(1);
			notify("Insights reset.", "success");
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setBusy("");
		}
	};

	const handleCampaigns = async ({ brandName, styleGuide, adExamples } = {}) => {
		setBusy("campaigns");
		try {
			const result = await gigaCampaigns({
				runId: id,
				brandName: brandName ?? run?.brand_name,
				styleGuide,
				adExamples,
			});
			setRun(result);
			setTab(2);
			notify("Campaigns generated.", "success");
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setBusy("");
		}
	};

	const handleCreateAds = async ({ brandName, styleGuide, creationKeywords, instructions } = {}) => {
		setBusy("campaigns-create");
		try {
			const result = await gigaCampaigns({
				runId: id,
				mode: "ads",
				brandName: brandName ?? run?.brand_name,
				styleGuide,
				creationKeywords,
				instructions,
			});
			setRun(result);
			setTab(2);
			notify("Ad drafts generated.", "success");
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setBusy("");
		}
	};

	const handleCampaignsReset = async () => {
		setBusy("campaigns");
		try {
			const result = await gigaCampaigns({ runId: id, reset: true });
			setRun(result);
			setTab(2);
			notify("Campaigns reset.", "success");
		} catch (error) {
			notify(error.response?.data?.message || error.message);
		} finally {
			setBusy("");
		}
	};

	if (loading) {
		return <GigaRunPageSkeleton />;
	}

	if (!run) {
		return (
			<Box p={2}>
				<Alert severity="error">Run not found.</Alert>
				<Button sx={{ mt: 2 }} onClick={() => navigate(`/explore/${services.giga.path}`)}>
					Back to GIGA
				</Button>
			</Box>
		);
	}

	const progress = run.progress || [];
	const seeds = run.seeds || [];
	const hasExplore = Boolean(run.ideas?.length);
	const rerunBtnSx = { textTransform: "none", fontWeight: 600, borderRadius: 1.5 };
	const formatMetaValue = (value) => {
		if (value == null || value === "") return "—";
		if (typeof value === "object") return JSON.stringify(value);
		return String(value);
	};

	const stepMetaForProcess = (process = "") => {
		const key = /insight/i.test(process)
			? "insights"
			: /campaign/i.test(process)
			? "campaigns"
			: /explore|created/i.test(process)
			? "explore"
			: "";
		const meta = key ? run.step_meta?.[key] : null;
		if (!meta || typeof meta !== "object") return null;
		const rows = Object.entries(meta).map(([label, value]) => [label, formatMetaValue(value)]);
		if (/failed/i.test(process) && run.last_error) {
			rows.unshift(["last_error", run.last_error]);
		}
		return rows.length ? { title: key, rows } : null;
	};
	const detailRows = [
		["Name", run.name || "—"],
		["Status", run.status || "draft"],
		["Keywords", `${seeds.length}`],
		["Country", run.country || "—"],
		["Language", run.language || "—"],
		["Brand", run.brand_name || "—"],
		["Ads account", run.ads_account?.name || "—"],
		["Customer ID", run.ads_account?.customerId || "—"],
		["Created", run.created_at ? moment(run.created_at).format("DD/MM/YY HH:mm") : "—"],
		["Updated", run.updated_at ? moment(run.updated_at).format("DD/MM/YY HH:mm") : "—"],
		["User", run.user_email || "—"],
		["Ideas", String(run.ideas?.length || 0)],
		["Clusters", String(run.clusters?.length || 0)],
	];

	const sectionAction = (key, { emptyLabel, rerunLabel, icon, onClick, hasData }) => (
		<Button
			variant={hasData ? "outlined" : "contained"}
			startIcon={busy === key ? <CircularProgress size={16} color="inherit" /> : icon}
			onClick={onClick}
			disabled={Boolean(busy)}
			sx={{ ...rerunBtnSx, alignSelf: "flex-start" }}>
			{hasData ? rerunLabel : emptyLabel}
		</Button>
	);

	const statusTone = {
		failed: { bg: "#FEF3F2", color: "#B42318", border: "#FECDCA" },
		campaigns: { bg: "#ECFDF3", color: "#067647", border: "#ABEFC6" },
		insights: { bg: "#EFF8FF", color: "#175CD3", border: "#B2DDFF" },
		explored: { bg: "#F4F3FF", color: "#5925DC", border: "#D9D6FE" },
		draft: { bg: grey[50], color: grey[700], border: grey[200] },
	};
	const tone = statusTone[run.status] || statusTone.draft;

	return (
		<Box sx={{ p: 2, maxWidth: 1400, mx: "auto", bgcolor: grey[50], minHeight: "100%" }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
				<Stack direction="row" alignItems="center" spacing={1} minWidth={0} flex={1}>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate(`/explore/${services.giga.path}`)}
						sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600, color: grey[700] }}>
						All runs
					</Button>
					<Box minWidth={0}>
						<Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
							<Typography
								variant="h5"
								noWrap
								title={run.name || "GIGA run"}
								sx={{ fontWeight: 600, letterSpacing: "-0.02em", color: grey[900] }}>
								{run.name || "GIGA run"}
							</Typography>
							<Button
								onClick={() => setDetailsOpen(true)}
								sx={{
									flexShrink: 0,
									textTransform: "none",
									fontWeight: 600,
									fontSize: 12.5,
									height: 28,
									px: 1.25,
									borderRadius: 1.5,
									color: grey[800],
									bgcolor: "#fff",
									border: `1px solid ${grey[300]}`,
									whiteSpace: "nowrap",
									boxShadow: "none",
									"&:hover": { bgcolor: grey[50], borderColor: grey[400], boxShadow: "none" },
								}}>
								View details
							</Button>
						</Stack>
						<Typography variant="body2" sx={{ color: grey[600], fontSize: 13.5 }} noWrap>
							{seeds.length} keyword{seeds.length === 1 ? "" : "s"}
							{run.country ? ` · ${run.country}` : ""}
							{run.ads_account?.name ? ` · ${run.ads_account.name}` : ""}
							{run.created_at ? ` · ${moment(run.created_at).format("DD/MM/YY HH:mm")}` : ""}
						</Typography>
					</Box>
				</Stack>
				<Chip
					size="small"
					label={run.status || "draft"}
					sx={{
						height: 22,
						fontWeight: 600,
						fontSize: 11.5,
						textTransform: "capitalize",
						bgcolor: tone.bg,
						color: tone.color,
						border: `1px solid ${tone.border}`,
						borderRadius: 1.25,
						"& .MuiChip-label": { px: 0.9 },
					}}
				/>
			</Stack>

			<Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2 }}>
				<Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ px: 1, borderBottom: "1px solid #e5e7eb" }}>
					<Tab label="Explore" />
					<Tab label="Insights" />
					<Tab label="Campaigns" />
					<Tab label="Progress" />
				</Tabs>
				<Box p={2} sx={{ position: "relative", minHeight: 200 }}>
					{busy && busy !== "insights" && !String(busy).startsWith("campaigns") && (
						<GigaBusyOverlay label="Generating keywords…" />
					)}
					{tab === 0 && (
						<Stack spacing={2}>
							<Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
								<Typography variant="body2" color="text.secondary">
									Keyword clusters from this run.
								</Typography>
								{sectionAction("explore", {
									emptyLabel: "Generate Explore",
									rerunLabel: "Rerun",
									icon: <ReplayIcon />,
									onClick: hasExplore ? () => setExploreRerunOpen(true) : handleExplore,
									hasData: hasExplore,
								})}
							</Stack>
							<GigaExploreView key={run.updated_at || run._id} run={run} />
						</Stack>
					)}

					{tab === 1 && <GigaInsightsView run={run} busy={busy} onAsk={handleInsights} onReset={handleInsightsReset} />}

					{tab === 2 && (
						<GigaCampaignsView
							run={run}
							busy={busy}
							onGenerate={handleCampaigns}
							onCreateAds={handleCreateAds}
							onReset={handleCampaignsReset}
						/>
					)}

					{tab === 3 && (
						<Stepper orientation="vertical" activeStep={progress.length}>
							{progress.map((step, index) => {
								const meta = stepMetaForProcess(step.process);
								return (
									<Step key={`${step.process}-${index}`} expanded active>
										<StepLabel>{step.label || step.process}</StepLabel>
										<StepContent>
											<Typography variant="body2" color="text.secondary">
												{step.message}
											</Typography>
											<Typography variant="caption" color="text.secondary" display="block">
												{step.created_at ? moment(step.created_at).format("DD/MM/YY HH:mm:ss") : ""}
											</Typography>
											{meta && (
												<Button
													onClick={() => setStepData(meta)}
													sx={{
														...rerunBtnSx,
														mt: 1,
														textTransform: "none",
														fontWeight: 600,
														fontSize: 12.5,
														height: 28,
														px: 1.25,
														color: grey[800],
														bgcolor: "#fff",
														border: `1px solid ${grey[300]}`,
													}}>
													View step data
												</Button>
											)}
										</StepContent>
									</Step>
								);
							})}
						</Stepper>
					)}
				</Box>
			</Paper>

			<Dialog
				open={Boolean(stepData)}
				onClose={() => setStepData(null)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: { borderRadius: 2 } }}>
				<DialogTitle sx={{ fontWeight: 800, pb: 1, textTransform: "capitalize" }}>
					{stepData?.title ? `${stepData.title} step data` : "Step data"}
				</DialogTitle>
				<DialogContent sx={{ pt: 0 }}>
					{(stepData?.rows || []).map(([label, value]) => (
						<Box
							key={label}
							sx={{
								display: "grid",
								gridTemplateColumns: "160px 1fr",
								gap: 1,
								py: 0.75,
								borderBottom: "1px solid #f1f5f9",
							}}>
							<Typography variant="body2" color="text.secondary">
								{label}
							</Typography>
							<Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
								{value}
							</Typography>
						</Box>
					))}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setStepData(null)} sx={{ textTransform: "none", fontWeight: 600 }}>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={detailsOpen}
				onClose={() => setDetailsOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: { borderRadius: 2 } }}>
				<DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{run.name || "Run details"}</DialogTitle>
				<DialogContent sx={{ pt: 0 }}>
					<Typography fontWeight={700} fontSize={13} mb={1}>
						Keywords ({seeds.length})
					</Typography>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 0.75,
							maxHeight: 240,
							overflowY: "auto",
							mb: 2,
							pr: 0.5,
						}}>
						{seeds.length ? (
							seeds.map((seed) => (
								<Chip key={seed} size="small" label={seed} sx={{ fontWeight: 600 }} />
							))
						) : (
							<Typography variant="body2" color="text.secondary">
								No keywords on this run.
							</Typography>
						)}
					</Box>
					<Divider sx={{ mb: 1.5 }} />
					<Typography fontWeight={700} fontSize={13} mb={0.5}>
						Run details
					</Typography>
					{detailRows.map(([label, value]) => (
						<Box
							key={label}
							sx={{
								display: "grid",
								gridTemplateColumns: "140px 1fr",
								gap: 1,
								py: 0.65,
								borderBottom: "1px solid #f1f5f9",
							}}>
							<Typography variant="body2" color="text.secondary">
								{label}
							</Typography>
							<Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
								{value}
							</Typography>
						</Box>
					))}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setDetailsOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={exploreRerunOpen}
				onClose={() => {
					if (busy) return;
					setExploreRerunOpen(false);
				}}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { borderRadius: 2 } }}>
				<DialogContent sx={{ pt: 3, pb: 1.5 }}>
					<Typography fontWeight={700} fontSize={18} mb={1}>
						Rerun Explore?
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
						Keywords for this run will be replaced. Insights and Campaigns will be cleared and need to be generated again.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button
						onClick={() => setExploreRerunOpen(false)}
						disabled={busy === "explore"}
						sx={{ textTransform: "none", fontWeight: 600 }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disableElevation
						onClick={handleExplore}
						disabled={busy === "explore"}
						startIcon={busy === "explore" ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
						sx={{ textTransform: "none", fontWeight: 700 }}>
						Confirm rerun
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default GigaRunPage;
